/*
 * Rebuy "buy more, save more" on the product page.
 *
 * The widget is a Rebuy app block: Rebuy renders it into the page after load,
 * out of its own data, so none of its markup exists in this theme. What it
 * offers is a set of tiers ("buy 2 3-packs, save 20% more") and, inside the
 * tier the customer has chosen, one size picker per item in that bundle --
 * which makes the theme's own size picker a second, disagreeing way to answer
 * the same question, and leaves the add to cart button quoting the price of a
 * single item.
 *
 * So while a widget is on the page and its active tier carries size pickers of
 * its own, this bridge:
 *
 *   1. hides the theme's size picker, quantity input and stock line (the CSS
 *      does the hiding; this only decides when),
 *   2. marks the widget's sold out sizes, which Rebuy's swatches say nothing
 *      about, from the product's own variant data,
 *   3. shows the active tier's total on the add to cart button and in the
 *      mobile sticky bar,
 *   4. adds the whole bundle when that button is pressed, rather than the one
 *      variant the product form was built around,
 *   5. remembers that this product has a widget, so the next visit holds space
 *      for it instead of drawing a picker it is about to throw away.
 *
 * Every one of those is gated on a widget actually being rendered and visible.
 * With no widget -- Rebuy off, no campaign for this product, the app failing
 * to load -- no class is set, no listener acts, and the page behaves exactly
 * as it did before.
 *
 * The tier discounts themselves are not this file's business: they are applied
 * to the cart, so the lines only have to arrive in it. What the bridge must
 * not do is add them twice, which is why the add to cart click is stopped
 * before it can become a form submit -- that submit is what the Rebuy smart
 * cart listens for.
 *
 * Loaded from sections/main-product.liquid.
 */
(function rebuyBuyMoreSaveMore() {
  'use strict';

  const WIDGET = '.rebuy-buy-more-save-more';
  const BLOCK = '.rebuy-buy-more-save-more__block';
  const ACTIVE_BLOCK = '.rebuy-buy-more-save-more__block--active';
  const ITEM = '.rebuy-buy-more-save-more__block-info-variant';
  const ITEM_LABEL = '.rebuy-buy-more-save-more__block-info-variant-label';
  const VARIANTS = '.rebuy-buy-more-save-more__block-info-variants';
  const SWATCH_GROUP = '.rebuy-product-options__button-swatches';
  const SWATCH = '.rebuy-product-options__button-swatch';
  const SALE_PRICE = '.rebuy-buy-more-save-more__block-info-price';
  const COMPARE_PRICE = '.rebuy-buy-more-save-more__block-info-compare-at-price';

  const ADD_BUTTON = 'product-info product-form [name="add"]';
  const STICKY_BUTTON = 'sticky-add-to-cart [data-sticky-add-to-cart]';

  /*
   * "Owns the options": the widget is visible and its active tier is showing
   * size pickers, so it -- not the theme -- decides what goes in the cart.
   * Nothing below acts while this is false.
   */
  let owns = false;
  let product;
  let pending;
  let adding = false;
  let remembered = null;

  window.rebuyBmsm = window.rebuyBmsm || {};
  window.rebuyBmsm.key = window.rebuyBmsm.key || `rebuy-bmsm:${window.location.pathname}`;

  const strings = window.rebuyBmsm.strings || {};
  const behavior = window.rebuyBmsm.addToCartBehavior || '';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function visible(element) {
    return !!(
      element &&
      (element.offsetWidth || element.offsetHeight || element.getClientRects().length)
    );
  }

  function widget() {
    const element = document.querySelector(WIDGET);
    return visible(element) ? element : null;
  }

  function activeTier(element) {
    /* Rebuy marks one block active; before it has, the first one is the one shown. */
    return element.querySelector(ACTIVE_BLOCK) || element.querySelector(BLOCK);
  }

  function items(tier) {
    return tier ? Array.from(tier.querySelectorAll(ITEM)) : [];
  }

  function swatches(item) {
    return Array.from(item.querySelectorAll(`${SWATCH_GROUP} input[type="radio"]`));
  }

  /* Product data ---------------------------------------------------------- */

  /*
   * Rebuy's swatches carry an option value and nothing else, so this is the
   * only way from "XL" to a variant id and to whether that variant is in
   * stock. Printed by snippets/rebuy-bmsm.liquid, with the variant picker's
   * own JSON as a fallback: the two say the same thing about stock, and a page
   * that has one but not the other should still mark its sold out sizes.
   */
  function productData() {
    if (product !== undefined) return product;
    product = fromSnippet() || fromVariantPicker() || null;
    return product;
  }

  function shape(variants, optionCount) {
    if (!Array.isArray(variants) || !variants.length) return null;
    return {
      optionCount: optionCount || (variants[0].options || []).length,
      variants: variants
        .filter((variant) => variant && Array.isArray(variant.options))
        .map((variant) => ({
          id: variant.id,
          options: variant.options,
          available: !!variant.available
        }))
    };
  }

  function parse(node) {
    if (!node) return null;
    try {
      /* Liquid prints this over several lines; the theme's own reader does the same. */
      return JSON.parse(node.textContent.replace(/[\n\r\t]/g, ' '));
    } catch (error) {
      return null;
    }
  }

  function fromSnippet() {
    const parsed = parse(document.querySelector('script[data-rebuy-bmsm-product]'));
    return parsed ? shape(parsed.variants, (parsed.options || []).length) : null;
  }

  function fromVariantPicker() {
    const parsed = parse(
      document.querySelector(
        'variant-radios script[type="application/json"], variant-selects script[type="application/json"]'
      )
    );
    return shape(parsed);
  }

  /*
   * Rebuy renders one swatch group per product option, in the product's own
   * option order, so a full set of chosen values matches a variant position by
   * position. The looser second pass covers a widget that only offers some of
   * the options -- a size picker on a product that also has a colour, say --
   * where the first variant carrying every chosen value is the best answer
   * available.
   */
  function variantFor(values) {
    const data = productData();
    if (!data || !values.length) return null;

    const exact = data.variants.find(
      (variant) =>
        variant.options.length === values.length &&
        variant.options.every((option, index) => option === values[index])
    );
    if (exact) return exact;

    return (
      data.variants.find((variant) => values.every((value) => variant.options.includes(value))) ||
      null
    );
  }

  /* What the customer has chosen ------------------------------------------ */

  function selection(item) {
    return Array.from(item.querySelectorAll(`${SWATCH_GROUP} input[type="radio"]:checked`)).map(
      (input) => input.value.trim()
    );
  }

  function chosenVariant(item) {
    const values = selection(item);
    return values.length ? variantFor(values) : null;
  }

  /* Sold out sizes -------------------------------------------------------- */

  /*
   * Rebuy's swatches are drawn from the option values alone, so a size that is
   * out of stock looks exactly like one that is not. The theme's own picker
   * greys those out, and hiding it must not lose that.
   *
   * Marked rather than removed: a sold out size still has to be clickable, so
   * size-suggestions.js can offer the same size on a product that has it.
   */
  function markSoldOut(tier) {
    const data = productData();
    if (!data) return;

    items(tier).forEach((item) => {
      const groups = Array.from(item.querySelectorAll(SWATCH_GROUP));
      const chosen = groups.map((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value.trim() : null;
      });

      groups.forEach((group, index) => {
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const swatch = input.closest(SWATCH);
          if (!swatch) return;

          const values = chosen.slice();
          values[index] = input.value.trim();

          let available;
          if (values.every(Boolean) && values.length === data.optionCount) {
            const variant = variantFor(values);
            available = !!(variant && variant.available);
          } else {
            /*
             * The other options are still open, so the question can only be
             * "is this value in stock in any combination" -- the same fallback
             * the size suggestions drawer makes.
             */
            available = data.variants.some(
              (variant) => variant.options[index] === input.value.trim() && variant.available
            );
          }

          swatch.classList.toggle('rebuy-bmsm-unavailable', !available);
        });
      });
    });
  }

  /* Prices ---------------------------------------------------------------- */

  /*
   * Rebuy pairs every price with a screen reader label inside the same
   * element ("Sale tier price: €47,97"), so the visible half has to be picked
   * out rather than the whole thing read.
   */
  function priceText(element) {
    if (!element) return '';

    const shown = element.querySelector('[aria-hidden]');
    if (shown) return shown.textContent.trim();

    const clone = element.cloneNode(true);
    clone.querySelectorAll('.sr-only').forEach((node) => node.remove());
    return clone.textContent.trim();
  }

  /*
   * Writes are guarded on the text actually differing. The mutation observer
   * below reacts to everything these touch, so an unconditional write would
   * keep waking it.
   *
   * An empty string is a write like any other: a tier with no compare-at price
   * has to clear the one Liquid rendered, or the product's own "was" price is
   * left standing next to a bundle total it has nothing to do with.
   */
  function setText(element, text) {
    if (!element) return;
    if (element.dataset.bmsmOriginal === undefined) {
      element.dataset.bmsmOriginal = element.textContent.trim();
    }
    if (element.textContent.trim() !== text) element.textContent = text;
  }

  /*
   * The tier total, in the two places the page quotes a price to press: the
   * add to cart button on desktop, and the sticky bar on mobile, where the
   * button's own price is hidden. The price block at the top of the column is
   * left alone on purpose -- that one is about the product, not about the
   * bundle being configured further down.
   */
  function applyPrices(tier) {
    const sale = priceText(tier.querySelector(SALE_PRICE));
    if (!sale) return;

    const compare = priceText(tier.querySelector(COMPARE_PRICE));

    document.querySelectorAll(`${ADD_BUTTON} .price`).forEach((element) => {
      setText(element, sale);
    });

    const sticky = document.querySelector('sticky-add-to-cart .product__price');
    if (!sticky) return;

    setText(
      sticky.querySelector('.price__item--sale') || sticky.querySelector('.price__item--regular'),
      sale
    );
    setText(sticky.querySelector('.price__item--compare'), compare);
  }

  /* Puts back whatever Liquid rendered, if the widget ever goes away. */
  function restorePrices() {
    document.querySelectorAll('[data-bmsm-original]').forEach((element) => {
      element.textContent = element.dataset.bmsmOriginal;
      delete element.dataset.bmsmOriginal;
    });
  }

  /* Keeping the rest of the page in step ---------------------------------- */

  /*
   * The hidden id the product form was built around, kept pointing at
   * something real. Only the first item of the bundle can be followed -- the
   * form holds one variant and a bundle can hold several sizes -- and it only
   * matters as a fallback: the add below posts every chosen size itself.
   *
   * Silent on purpose. Checking the theme's own radio instead would be a
   * tidier way to do this, but it sets off <variant-radios>, which re-renders
   * the price block and rewrites ?variant in the URL -- and Rebuy watches the
   * page's variant to decide whether to rebuild the widget. Rebuilding it
   * throws away every size chosen after the first one, so the theme's picker
   * is left alone while the widget is in charge. The stock line goes with it:
   * it describes one variant, which is no longer what is being bought.
   */
  function syncFormVariant(tier) {
    const first = items(tier)[0];
    const variant = first ? chosenVariant(first) : null;
    if (!variant) return;

    document.querySelectorAll('product-info form input[name="id"]').forEach((input) => {
      if (input.value !== String(variant.id)) input.value = variant.id;
    });
  }

  /* Missing sizes --------------------------------------------------------- */

  function clearNote(tier) {
    tier.querySelectorAll('.rebuy-bmsm-missing').forEach((item) => {
      item.classList.remove('rebuy-bmsm-missing');
    });
    tier.querySelector('[data-rebuy-bmsm-note]')?.remove();
  }

  function note(tier, missing) {
    clearNote(tier);
    missing.forEach((item) => item.classList.add('rebuy-bmsm-missing'));

    const message = document.createElement('p');
    message.className = 'rebuy-bmsm__note';
    message.setAttribute('data-rebuy-bmsm-note', '');
    message.setAttribute('role', 'alert');
    message.textContent = strings.chooseOption || '';
    (tier.querySelector(VARIANTS) || tier).append(message);

    const target = (missing[0] && missing[0].querySelector(ITEM_LABEL)) || tier;
    const rect = target.getBoundingClientRect();
    if (rect.top < 0 || rect.bottom > window.innerHeight) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /* Adding the bundle ----------------------------------------------------- */

  /*
   * One line per distinct variant: two items in the same size are one line of
   * two, which is both what the cart would collapse them into anyway and what
   * the tier discount is counted against.
   */
  function lines(tier) {
    const missing = [];
    const quantities = new Map();

    items(tier).forEach((item) => {
      const variant = chosenVariant(item);
      if (!variant || !variant.available) {
        missing.push(item);
        return;
      }
      quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);
    });

    return {
      missing,
      items: Array.from(quantities, ([id, quantity]) => ({ id, quantity }))
    };
  }

  async function addBundle(button) {
    if (adding) return;

    const element = widget();
    const tier = element && activeTier(element);
    if (!tier) return;

    const bundle = lines(tier);
    if (bundle.missing.length || !bundle.items.length) {
      note(tier, bundle.missing.length ? bundle.missing : items(tier));
      return;
    }

    clearNote(tier);
    track(button);
    adding = true;
    button?.classList.add('button--loading');
    button?.setAttribute('disabled', 'true');

    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: bundle.items })
      });
      if (!response.ok) throw new Error(response.status);

      await openCart();
    } catch (error) {
      /* Nothing is changed on the page, so the button can simply be pressed again. */
    } finally {
      adding = false;
      button?.classList.remove('button--loading');
      button?.removeAttribute('disabled');
    }
  }

  /*
   * Whichever cart the store is set to. The smart cart is the usual one here
   * and it reads the cart back itself, so it only has to be told to open; the
   * theme's own drawer has to be re-rendered first.
   */
  async function openCart() {
    const smartCart = window.Rebuy && window.Rebuy.SmartCart;
    if (behavior === 'rebuy_smart_cart' && smartCart) {
      try {
        if (typeof smartCart.refresh === 'function') smartCart.refresh();
        if (typeof smartCart.show === 'function') {
          smartCart.show();
          /* Not awaited: the flyout is already open, the count can catch up. */
          refreshCartCount();
          return;
        }
      } catch (error) {
        /* Fall through to the theme's own cart below. */
      }
    }

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      refreshCartCount();
      return;
    }

    try {
      const response = await fetch(`${root()}?sections=cart-drawer,cart-icon-bubble`);
      if (!response.ok) return;

      /* Re-rendered sections include the icon bubble, so the count comes free. */
      drawer.renderContents({ sections: await response.json() });
      drawer.open();
    } catch (error) {
      /* The line is in the cart either way; only the drawer missed it. */
      refreshCartCount();
    }
  }

  /* The header count, which only the theme's own cart keeps up to date. */
  async function refreshCartCount() {
    try {
      const response = await fetch(`${root()}cart.js`);
      if (!response.ok) return;

      const cart = await response.json();
      const count = document.querySelector('.cart-count');
      if (count) count.textContent = cart.item_count > 0 ? cart.item_count : '';
      if (cart.item_count > 0) {
        document.querySelector('.cart-icon-bubble__count')?.classList.remove('hidden');
      }
    } catch (error) {
      /* A stale count is not worth failing an add over. */
    }
  }

  /* Taking over add to cart ----------------------------------------------- */

  /*
   * Capture phase, and the click stops here. preventDefault alone would be
   * enough to stop the form submit that product-form.js and the Rebuy smart
   * cart both listen for, but the listeners bound to the button itself would
   * still run: the sticky button would open its own sheet of sizes -- the very
   * picker the widget replaces -- and anything that adds to the cart on the
   * click rather than on the submit would add a single variant next to the
   * bundle. Neither is worth risking, so the one listener on that button that
   * matters is called by hand below instead.
   */
  function onClick(event) {
    if (!owns) return;

    const button = event.target.closest(`${STICKY_BUTTON}, ${ADD_BUTTON}`);
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    addBundle(button);
  }

  /*
   * The Klaviyo "Added to cart" event snippets/klaviyo-tracking.liquid binds to
   * this button, sent from the same data attributes it reads. Sent from here
   * it is also better timed than the original: on the sticky button that
   * listener fires when the size sheet opens, before anything has been added.
   */
  function track(button) {
    const learnq = window._learnq;
    if (!learnq || !button || !button.dataset.trackAddToCartName) return;

    const data = button.dataset;
    const categories = (data.trackAddToCartCategories || '').split(',').filter(Boolean);

    learnq.push([
      'track',
      'Added to cart',
      {
        Name: data.trackAddToCartName || '',
        ProductID: data.trackAddToCartProductId || '',
        Categories: categories.length ? categories : null,
        ImageURL: data.trackAddToCartImageUrl || '',
        URL: data.trackAddToCartUrl || '',
        Brand: data.trackAddToCartBrand || '',
        Price: data.trackAddToCartPrice || '',
        Locale: ((window.Shopify && window.Shopify.locale) || '').toUpperCase(),
        CompareAtPrice: data.trackAddToCartCompareAtPrice || ''
      }
    ]);
  }

  /*
   * A backstop for a submit that never was a click on the button: anything
   * calling form.submit() or requestSubmit() directly. Disjoint from the click
   * above -- a prevented click produces no submit -- so the two cannot both
   * add the same bundle.
   */
  function onSubmit(event) {
    if (!owns) return;

    const form = event.target;
    if (!form.closest || !form.closest('product-info')) return;
    if (!(form.action || '').includes('/cart/add')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addBundle(form.querySelector('[name="add"]'));
  }

  /* The placeholder ------------------------------------------------------- */

  /*
   * Rebuy answers a second or two after the page does, and the product column
   * has to decide what to draw in the meantime. snippets/rebuy-bmsm.liquid
   * holds space for the widget, and hides the picker it is about to replace,
   * on any product this browser has already seen one on -- so the wait reads
   * as something loading rather than as the page rearranging itself.
   *
   * This is the other half of that: remembering the widget, and getting out of
   * the way once the wait is over.
   */
  function settle() {
    document.documentElement.classList.remove('rebuy-bmsm-expected');
  }

  /*
   * Its height as well as its presence, so next time the space held is the
   * space needed. Rounded, and only written when it has really moved, to keep
   * a resizing widget from writing on every frame.
   */
  function remember(element) {
    const height = Math.min(900, Math.max(120, Math.round(element.getBoundingClientRect().height / 10) * 10));
    if (!height || height === remembered) return;

    remembered = height;
    try {
      window.localStorage.setItem(window.rebuyBmsm.key, String(height));
    } catch (error) {
      /* Storage off: the placeholder is a nicety, never a requirement. */
    }
  }

  /*
   * Forgotten as soon as the widget fails to turn up, or turns up without
   * pickers of its own: either way the picker this hides is the one the
   * customer needs, and no later visit should hide it again.
   */
  function forget() {
    remembered = null;
    try {
      window.localStorage.removeItem(window.rebuyBmsm.key);
    } catch (error) {
      /* Nothing was stored in the first place. */
    }
  }

  /* Staying in sync ------------------------------------------------------- */

  function sync() {
    const element = widget();

    if (!element) {
      if (owns) {
        owns = false;
        document.body.classList.remove('rebuy-bmsm-owns-options');
        restorePrices();
      }
      return;
    }

    /* There is a widget to look at now, so there is nothing left to hold space for. */
    settle();

    const tier = activeTier(element);
    const tierItems = items(tier);

    /*
     * A tier with no pickers of its own -- a single item bundle Rebuy expects
     * the page to configure, or a widget still rendering -- means the theme's
     * picker is still the one in charge, and everything here stands down.
     */
    owns = tierItems.length > 0 && tierItems.every((item) => swatches(item).length > 0);
    document.body.classList.toggle('rebuy-bmsm-owns-options', owns);

    if (!owns) {
      restorePrices();
      forget();
      return;
    }

    remember(element);
    markSoldOut(tier);
    applyPrices(tier);
    syncFormVariant(tier);

    /* The prompt has served its purpose once every item has a size. */
    if (tierItems.every((item) => chosenVariant(item))) clearNote(tier);
  }

  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(sync, 60);
  }

  /*
   * Rebuy re-renders the widget from scratch on a tier change, and the theme
   * re-renders the price block on a variant change, so both sides of this are
   * markup that comes and goes. Watching is simpler, and steadier, than
   * guessing which event lands after which render.
   *
   * Every write sync() makes is guarded on the value actually changing, so the
   * mutations it causes itself settle after one pass instead of looping.
   */
  function observe() {
    const observer = new MutationObserver(schedule);
    const options = {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    };

    const targets = new Set(document.querySelectorAll('[data-rebuy-id]'));
    const info = document.querySelector('product-info');
    const sticky = document.querySelector('sticky-add-to-cart');
    if (info) targets.add(info);
    if (sticky) targets.add(sticky);

    targets.forEach((target) => observer.observe(target, options));
  }

  let started = false;

  /*
   * Nothing is bound on a page with no Rebuy block on it -- which is the whole
   * of the "with no widget, nothing changes" promise at the top of this file.
   * The block is server rendered by the app, so it is normally there from the
   * first call; the later triggers below cover a store where it is not.
   */
  function start() {
    if (started) return;

    if (!document.querySelector('[data-rebuy-id]')) {
      /* The block is gone from the template; anything held for it is stale. */
      if (document.readyState === 'complete') {
        settle();
        forget();
      }
      return;
    }

    started = true;

    /*
     * However slow Rebuy is, the placeholder does not outlast this: a campaign
     * that has since ended would otherwise leave the page with no size picker
     * at all. Six seconds is well past a normal render and still short enough
     * to be a hiccup rather than a broken page.
     */
    setTimeout(() => {
      settle();
      if (!owns) forget();
    }, 6000);

    observe();
    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);
    /* Rebuy's swatches are plain radios, so a pick is an ordinary change event. */
    document.addEventListener('change', schedule);
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('load', start);
  ['rebuy.beforeReady', 'rebuy.ready'].forEach((event) =>
    document.addEventListener(event, start)
  );
})();
